// Typy biura podawczego — przesyłki wychodzące: kategorie/zakładki, karty nadawcze
// (DispatchChart) i zwrotki ZPO. Odwzorowanie modułu wychodzących z Magistratu.

import { IKey } from "dto/IKey";

/** Kategoria formy doręczenia (BE ParcelDeliveryCategory) — steruje zakładką. */
export enum EParcelDeliveryCategory {
  Post = 0,
  PersonalPickup = 1,
  PersonalDelivery = 2,
  EDelivery = 3,
  Epuap = 4,
  Email = 5,
  Courier = 6,
  /** Elektroniczne Biuro Interesanta (kanał EBI). */
  Ebi = 7,
}

/** Podgrupa Poczty (BE ParcelPostGroup) — polecone/ZPO vs zwykłe. */
export enum EParcelPostGroup {
  RegisteredZpo = 0,
  Plain = 1,
}

export interface IOutgoingPostGroup {
  PostGroup: number;
  Name: string;
  Count: number;
  DeliveryKindIds: number[];
}

export interface IOutgoingCategory {
  Category: number;
  Name: string;
  SupportsDispatchChart: boolean;
  SupportsReceptionConfirmation: boolean;
  ReadyToDispatchCount: number;
  ChartCount: number;
  PendingReceptionCount: number;
  DeliveryKindIds: number[];
  PostGroups: IOutgoingPostGroup[];
}

export interface IOutgoingCategoriesResponse {
  Categories: IOutgoingCategory[];
  /** Przesyłki hybrydowe (PUH) nadane w bieżącym miesiącu — badge skrzynki „Wysłane PUH". */
  PuhSentCount?: number;
}

export interface IDispatchListItem {
  Id: string;
  ParcelNumber?: string | null;
  Title?: string | null;
  ReceiverName?: string | null;
  ReceiverAddress?: string | null;
  DeliveryKindId?: number | null;
  DeliveryKindName?: string | null;
  CaseSignature?: string | null;
  DocumentDate?: string | null;
  EditorDocumentId?: string | null;
  AuthorName?: string | null;
  /** Koperta wspólna (przesyłka grupująca) — wiersz rozwijalny. */
  IsGroupingParcel?: boolean;
  /** Pisma zgrupowane w kopercie (tylko dla IsGroupingParcel). */
  GroupedParcels?: IDispatchListItem[];
  /**
   * Adresat ma w książce adresowej nowsze dane niż te w przesyłce (koperta — także w jej
   * pismach); przenosi je akcja „Aktualizuj dane adresata”.
   */
  ReceiverDataOutdated?: boolean;
}

export interface IDispatchListResponse {
  Items: IDispatchListItem[];
  IsTeamView: boolean;
}

export interface IDispatchChartParcel {
  Id: string;
  ParcelNumber?: string | null;
  Title?: string | null;
  ReceiverName?: string | null;
  ReceiverAddress?: string | null;
  Fee?: number | null;
  AuthorName?: string | null;
  IsGroupingParcel?: boolean;
  GroupedParcels?: IDispatchChartParcel[];
}

export interface IDispatchChart {
  Id: string;
  Signature: string;
  DeliveryKindId: number;
  DeliveryKindName?: string | null;
  ChartTypeCode?: string | null;
  Number: number;
  Year: number;
  CreationDate: string;
  DispatchDate?: string | null;
  IsFake: boolean;
  ParcelCount: number;
  CreatorName?: string | null;
  SenderName?: string | null;
  Parcels: IDispatchChartParcel[];
}

export interface IDispatchChartListResponse {
  Charts: IDispatchChart[];
  IsTeamView: boolean;
}

export interface IPendingReceptionItem {
  Id: string;
  ParcelNumber?: string | null;
  Title?: string | null;
  ReceiverName?: string | null;
  DispatchDate?: string | null;
  DeliveryKindId?: number | null;
  AuthorName?: string | null;
}

export interface IPendingReceptionResponse {
  Items: IPendingReceptionItem[];
  IsTeamView: boolean;
}

// ── Requesty ──

export interface IDispatchListRequest {
  Category: number;
  PostGroup?: number | null;
}

export interface IPrepareDispatchChartRequest {
  ParcelIds: string[];
  SendDate?: string | null;
  IsFake: boolean;
}

export interface IPrepareDispatchChartResponse {
  ChartId: string;
  Signature: string;
}

export interface IProcessDispatchChartRequest {
  ChartId: string;
  DispatchDate: string;
  DispatchNoteFileUri?: string | null;
  DispatchNoteKeyId?: string | null;
}

export interface IDispatchChartListRequest {
  Category?: number | null;
  IncludeDispatched?: boolean;
}

export interface IRemoveFromChartRequest {
  ChartId?: string | null;
  ParcelIds?: string[] | null;
}

export interface IReceptionConfirmationRequest {
  ParcelId: string;
  ReceptionDate: string;
  IsReturned: boolean;
  ReturnAnnotation?: string | null;
  ScanFileUri?: string | null;
  ScanKeyId?: string | null;
}

export interface IPendingReceptionRequest {
  Category?: number | null;
}

/** Masowa zmiana sposobu nadania — ta sama forma dla wielu przesyłek. */
export interface IChangeDeliveryKindManyRequest {
  ParcelIds: string[];
  DeliveryKindId: number;
}

export interface IChangeDeliveryKindFailure {
  ParcelId: string;
  ParcelNumber?: string | null;
  Message: string;
}

export interface IChangeDeliveryKindManyResponse {
  Changed: number;
  Failed: IChangeDeliveryKindFailure[];
}

// ── Przesyłka grupująca (koperta wspólna) ──

export interface IGroupParcelsRequest {
  ParcelIds: string[];
  /** Forma doręczenia koperty (domyślnie forma pierwszej przesyłki). */
  DeliveryKindId?: number | null;
  /** Przesyłka, której adresat staje się adresatem koperty. */
  ReceiverParcelId?: string | null;
}

export interface IGroupParcelsResponse {
  ParcelId: string;
  ParcelNumber?: string | null;
  GroupedCount: number;
}

export interface IUngroupParcelsRequest {
  ParcelId: string;
}

// ── „Aktualizuj dane adresata” ──

export interface IRefreshReceiverDataRequest {
  /** Przesyłki wychodzące (koperta wspólna obejmuje też swoje pisma). */
  ParcelIds: string[];
}

export interface IRefreshReceiverDataItem {
  ParcelId: string;
  ParcelNumber?: string | null;
  Message?: string | null;
}

export interface IRefreshReceiverDataResponse {
  /** Przesyłki, którym przypięto nowsze dane adresata. */
  Updated: number;
  /** Przesyłki, które miały już aktualne dane. */
  Unchanged: number;
  /** Pominięte (nadane, na karcie nadawczej, pismo z koperty…) z powodem. */
  Failed: IRefreshReceiverDataItem[];
  /** Zaktualizowane, ale z treścią pisma wygenerowaną wcześniej — pismo do ponownego wygenerowania. */
  RegenerateLetters: IRefreshReceiverDataItem[];
}

// ── Skrzynka „Wysłane PUH" ──

export interface IPuhSentRequest {
  Year: number;
  Month: number;
}

export interface IPuhSentItem {
  Id: string;
  ParcelNumber?: string | null;
  Title?: string | null;
  ReceiverName?: string | null;
  ReceiverAddress?: string | null;
  SendDate?: string | null;
  DeliveredDate?: string | null;
  StatusName?: string | null;
  /** BE StatusGlobalType: 5 Send_Sent, 30 EDeliveryAccepted, 31 EDeliveryRejected, 40 EDeliveryReceived. */
  StatusType?: number | null;
  Registered: boolean;
  ProofOfDelivery: boolean;
  AuthorName?: string | null;
}

export interface IPuhSentResponse {
  Items: IPuhSentItem[];
  IsTeamView: boolean;
}

export interface IResendParcelRequest {
  ParcelId: string;
}

export interface IResendParcelResponse {
  NewParcelId: string;
  ParcelNumber?: string | null;
}

// Status przesyłek wygenerowanych z pisma (widok Dysku / CaseDossier).
export enum EParcelDispatchStage {
  ReadyToDispatch = 0,
  InChart = 1,
  Sent = 2,
  Received = 3,
  Returned = 4,
  /** Podpis tradycyjny — wygenerowane, czeka na „Przekaż do wysyłki" (Projekty pism). */
  AwaitingRelease = 5,
}

/** Dowód elektroniczny przesyłki wychodzącej (ePUAP UPP/UPD, gov UPO/EPO) — plik jawny. */
export interface IParcelEvidence {
  Id: string;
  Source: "epuap" | "gov" | string;
  Type?: string | null;
  Label: string;
  Date?: string | null;
  HasFile: boolean;
  FileName?: string | null;
}

export interface IDocumentParcel {
  Id: string;
  ParcelNumber?: string | null;
  ReceiverName?: string | null;
  DeliveryKindName?: string | null;
  /** Kategoria formy doręczenia (EParcelDeliveryCategory) — etykieta potwierdzenia. */
  DeliveryCategory?: number | null;
  Stage: number;
  StageLabel: string;
  DispatchDate?: string | null;
  ReceivedDate?: string | null;
  IsReturned?: boolean;
  ReturnAnnotation?: string | null;
  /** Jest skan zwrotki ZPO / potwierdzenia odbioru (szyfrowany kluczem firmy). */
  HasReceptionConfirmation?: boolean;
  /** Jest skan karty nadawczej (potwierdzenie nadania). */
  HasDispatchNote?: boolean;
  Evidences?: IParcelEvidence[];
}

export interface IDocumentParcels {
  HasParcels: boolean;
  MajorSignature?: string | null;
  Parcels: IDocumentParcel[];
  /** Pismo przekazane innej osobie do podpisu, jeszcze niepodpisane (przed generacją przesyłek). */
  PendingSign?: boolean;
  PendingSignerName?: string | null;
  PendingSince?: string | null;
}

// Podgląd treści przesyłki (wygenerowane pismo / PDF) — plik + klucze do deszyfrowania.
export interface IParcelPreview {
  Uri: string;
  EncryptionKeyId: string | null;
  Kind: number;
  FileName?: string | null;
  /** Typ MIME (skany potwierdzeń bywają obrazami). */
  ContentType?: string | null;
  ObjectKeys: IKey[];
  Keys: IKey[];
}

// Potwierdzenia doręczenia przesyłki wychodzącej — metryka pisma / przesyłki
// (Magistrat: „Podgląd ZPO", „Podgląd potwierdzenia nadania", UPP/UPD, dowody e-Doręczeń).
export enum EParcelConfirmationFileKind {
  Reception = 0,
  DispatchNote = 1,
}

export interface IParcelDeliveryInfo {
  ParcelId: string;
  ParcelNumber?: string | null;
  /** Przesyłka z obiegu biura podawczego — sekcja doręczenia ma sens; inaczej tylko gdy są potwierdzenia. */
  ViaMailroom: boolean;
  DeliveryKindName?: string | null;
  DeliveryCategory?: number | null;
  DispatchDate?: string | null;
  ChartSignature?: string | null;
  HasDispatchNote: boolean;
  ReceivedDate?: string | null;
  IsReturned: boolean;
  ReturnAnnotation?: string | null;
  HasReceptionConfirmation: boolean;
  PickupByName?: string | null;
  PickupNotCollected: boolean;
  Evidences: IParcelEvidence[];
}
